import React, { useState } from 'react'
import { Calendar, User, Clock, MessageCircle, ThumbsUp, Send } from 'lucide-react'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import AuthorCard from './AuthorCard'
import RelatedPosts from './RelatedPosts'

const BlogPost = ({ post, onPostSelect }) => {
  const [comments, setComments] = useState([])
  const [showComments, setShowComments] = useState(false)
  const { register, handleSubmit, formState: { errors }, reset } = useForm()

  // Load comments from localStorage
  React.useEffect(() => {
    const savedComments = localStorage.getItem(`shikdar_films_comments_${post.id}`)
    if (savedComments) {
      setComments(JSON.parse(savedComments))
    }
  }, [post.id])

  const onSubmitComment = (data) => {
    const newComment = {
      id: Date.now(),
      ...data,
      timestamp: new Date().toISOString(),
      likes: 0,
      replies: []
    }

    const updatedComments = [...comments, newComment]
    setComments(updatedComments)
    localStorage.setItem(`shikdar_films_comments_${post.id}`, JSON.stringify(updatedComments))
    
    toast.success('Comment posted successfully!')
    reset()
  }

  const handleLike = (commentId) => {
    const updatedComments = comments.map(comment => 
      comment.id === commentId 
        ? { ...comment, likes: comment.likes + 1 }
        : comment
    )
    setComments(updatedComments)
    localStorage.setItem(`shikdar_films_comments_${post.id}`, JSON.stringify(updatedComments))
  }

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' }
    return new Date(dateString).toLocaleDateString('en-US', options)
  }

  const getTimeAgo = (timestamp) => {
    const now = new Date()
    const commentDate = new Date(timestamp)
    const diffInMinutes = Math.floor((now - commentDate) / 60000)
    
    if (diffInMinutes < 60) return `${diffInMinutes} minutes ago`
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)} hours ago`
    return `${Math.floor(diffInMinutes / 1440)} days ago`
  }

  const handleImageError = (e) => {
    e.target.src = 'https://images.pexels.com/photos/7991579/pexels-photo-7991579.jpeg?auto=compress&cs=tinysrgb&w=800'
  }

  return (
    <article className="bg-white rounded-xl shadow-md overflow-hidden">
      {/* Featured Image */}
      <div className="relative h-96 overflow-hidden">
        <img 
          src={post.image}
          alt={post.title}
          onError={handleImageError}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
        <div className="absolute bottom-8 left-8 right-8">
          <span className="inline-block px-3 py-1 bg-bengal-green text-white rounded-full text-sm font-semibold mb-4">
            {post.category.replace('-', ' ').toUpperCase()}
          </span>
          <h1 className="text-4xl font-bold text-white mb-4 section-title">
            {post.title}
          </h1>
          <div className="flex items-center gap-4 text-white/90">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              <span className="text-sm">{formatDate(post.date)}</span>
            </div>
            {post.readTime && (
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span className="text-sm">{post.readTime}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-8 max-w-4xl mx-auto">
        <div className="prose prose-lg max-w-none mb-12">
          <p className="text-gray-700 leading-relaxed text-lg">
            {post.excerpt}
          </p>
        </div>

        {/* Author Card */}
        <AuthorCard post={post} />

        {/* Related Posts Section */}
        <RelatedPosts currentPost={post} onPostSelect={onPostSelect} />

        {/* Comments Section */}
        <div className="mt-12 border-t border-gray-200 pt-8">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-2xl font-bold text-gray-900 section-title">
              Discussion ({comments.length})
            </h3>
            <button
              onClick={() => setShowComments(!showComments)}
              className="flex items-center gap-2 text-bengal-green hover:text-bengal-orange font-medium transition-colors"
            >
              <MessageCircle className="w-5 h-5" />
              {showComments ? 'Hide Comments' : 'Show Comments'}
            </button>
          </div>

          {showComments && (
            <>
              {/* Comment Form */}
              <form onSubmit={handleSubmit(onSubmitComment)} className="mb-8 bg-gray-50 rounded-lg p-6">
                <h4 className="font-semibold text-gray-900 mb-4">Leave a Comment</h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <input
                      type="text"
                      {...register('name', { required: 'Name is required' })}
                      placeholder="Your Name"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bengal-green focus:border-transparent"
                    />
                    {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>}
                  </div>
                  
                  <div>
                    <input
                      type="email"
                      {...register('email', { 
                        required: 'Email is required',
                        pattern: { value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, message: 'Invalid email' }
                      })}
                      placeholder="Your Email"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bengal-green focus:border-transparent"
                    />
                    {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>}
                  </div>
                </div>

                <div className="mb-4">
                  <textarea
                    rows="4"
                    {...register('comment', { required: 'Comment is required' })}
                    placeholder="Share your thoughts..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bengal-green focus:border-transparent resize-none"
                  ></textarea>
                  {errors.comment && <p className="mt-1 text-sm text-red-600">{errors.comment.message}</p>}
                </div>

                <button
                  type="submit"
                  className="inline-flex items-center gap-2 bg-bengal-green hover:bg-bengal-orange text-white font-semibold px-6 h-11 rounded-lg shadow-md hover:shadow-lg transition-all duration-200"
                >
                  <Send className="w-4 h-4" />
                  Post Comment
                </button>
              </form>

              {/* Comments List */}
              <div className="space-y-6">
                {comments.length > 0 ? (
                  comments.map((comment) => (
                    <div key={comment.id} className="bg-gray-50 rounded-lg p-6">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-bengal-green rounded-full flex items-center justify-center flex-shrink-0">
                          <User className="w-6 h-6 text-white" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-2">
                            <div>
                              <h5 className="font-semibold text-gray-900">{comment.name}</h5>
                              <p className="text-sm text-gray-500">{getTimeAgo(comment.timestamp)}</p>
                            </div>
                            <button
                              onClick={() => handleLike(comment.id)}
                              className="flex items-center gap-1 text-gray-500 hover:text-bengal-green transition-colors"
                            >
                              <ThumbsUp className="w-4 h-4" />
                              <span className="text-sm">{comment.likes}</span>
                            </button>
                          </div>
                          <p className="text-gray-700 leading-relaxed">{comment.comment}</p>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <MessageCircle className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No comments yet. Be the first to share your thoughts!</p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </article>
  )
}

export default BlogPost